import { z } from 'zod';
import {
  CVSS_METRIC_REASON_SOURCES,
  CVSS_VECTOR_ORIGINS,
} from '@terra/shared-cvss';
import { FindingTypeFieldsSchema } from './finding-type.schema';
import {
  AssetReferenceSchema,
  CvssDataSchema,
  ExternalReferenceSchema,
  FindingSeveritySchema,
  FindingStatusSchema,
  JsonValueSchema,
  OwaspRiskDataSchema,
  PocBlocksSchema,
  SignalCategorySchema,
  TargetTypeSchema,
  InfraImpactSchema,
  BusinessRiskSchema,
} from '@terra/shared-schemas-orchestrator';

export const AUTO_PUBLISH_AFTER_CVSS_SCORE_METADATA_KEY =
  'autoPublishAfterCvssScore';

export const FindingCvssMetricReasonSchema = z.object({
  metric: z.string().min(1),
  value: z.string().min(1),
  reason: z.string(),
  source: z.enum(CVSS_METRIC_REASON_SOURCES),
});

export type FindingCvssMetricReason = z.infer<
  typeof FindingCvssMetricReasonSchema
>;

export const FindingCvssDataSchema = CvssDataSchema.extend({
  environmentalScore: z.string().nullish(),
  environmentalVector: z.string().nullish(),
  metricReasons: z.array(FindingCvssMetricReasonSchema).optional(),
  explanation: z.string().nullish(),
  vectorOrigin: z.enum(CVSS_VECTOR_ORIGINS).nullish(),
});

export const FindingFileInputSchema = z.object({
  name: z.string(),
  mimeType: z.string().optional(),
  content: z.string().describe('Base64 encoded file content'),
  size: z.number(),
});

export type FindingFileInput = z.infer<typeof FindingFileInputSchema>;

export const FindingPublicationSchema = z
  .object({
    decision: z.enum([
      'AUTO_PUBLISHED',
      'CATEGORY_NOT_ALLOWLISTED',
      'TARGET_NOT_WEB_APPLICATION',
      'FINDING_CHAINING_ORIGIN',
      'CVSS_UNSCORED',
      'UNKNOWN_ORIGIN',
      'FEATURE_FLAG_DISABLED',
      'FLAG_LOOKUP_FAILED',
      'NON_NEO_SOURCE',
    ]),
    publicationMode: z.enum(['auto_published', 'human_approved']).optional(),
    autoPublishFindingsEnabled: z.boolean().optional(),
    autoPublishAllowlistHash: z.string(),
  })
  .strict();

export type FindingPublication = z.infer<typeof FindingPublicationSchema>;

/**
 * Wire body for creating a finding. Hand-rolled to expose only writable
 * fields; server-managed fields (id, timestamps, encryption envelope) are
 * derived in the service layer.
 */
export const FindingCreateBodyDtoSchema = z.object({
  targetId: z.string(),
  targetType: TargetTypeSchema,
  signalId: z.string().nullable().optional(),
  executionStateId: z.string().nullable().optional(),
  traceThreadId: z.string().nullable().optional(),
  postAttackTraceId: z.string().nullable().optional(),
  status: FindingStatusSchema,
  severity: FindingSeveritySchema,
  category: SignalCategorySchema.nullable().optional(),
  cweIds: z.array(z.string()).optional(),
  owaspCategory: z.string().nullable().optional(),
  title: z.string(),
  shortDescription: z.string().nullable().optional(),
  assignedTo: z.string().optional(),
  filePath: z.string().nullable().optional(),
  metadata: JsonValueSchema.nullable().optional(),

  description: z.string().nullable().optional(),
  poc: z.string().nullable().optional(),
  pocBlocks: PocBlocksSchema.nullable().optional(),
  businessImpact: z.string().nullable().optional(),
  attackCapabilities: z.string().nullable().optional(),
  mitigationPlan: z.string().nullable().optional(),
  findingReport: z.string().nullable().optional(),
  exploitationGuide: z.string().nullable().optional(),
  executionScript: z.string().nullable().optional(),
  pocScript: z.string().nullable().optional(),

  isPublished: z.boolean().nullable().optional(),

  cvss: FindingCvssDataSchema.nullable().optional(),
  owaspRisk: z
    .lazy(() => OwaspRiskDataSchema)
    .nullable()
    .optional(),
  assets: z.array(AssetReferenceSchema).optional(),
  externalReferences: z.array(ExternalReferenceSchema).optional(),

  constituentFindingIds: z.array(z.string()).optional(),

  files: z.array(FindingFileInputSchema).optional(),
  infraImpact: InfraImpactSchema.nullable().optional(),
  businessRisk: BusinessRiskSchema.nullable().optional(),
  ...FindingTypeFieldsSchema.shape,
  publication: FindingPublicationSchema.optional(),
});

export type FindingCreateBodyDto = z.infer<typeof FindingCreateBodyDtoSchema>;
